// App.js
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './Components/Login/Login';
import ForgetPassword from './Components/Login/ForgetPassword/ForgetPwd';
import OtpVerify from './Components/Login/ForgetPassword/OTPpage';
import ResetPassword from './Components/Login/ForgetPassword/ResetPassword';
import Layout from './Components/Layout/Layout';
import Dashboard from './Components/Layout/Pages/Dashboard';
import Events from './Components/Layout/Pages/EventsManagement/EventsManagement';
import PaymentOverView from './Components/Layout/Pages/PaymentOverView/PaymentOverView';
import AttributeMaster from './Components/Layout/Pages/Master/AttributeMaster';
import CoordinatorDetails from './Components/Layout/Pages/Master/CoordinatorDetails';
import NotFound from './Components/Layout/Pages/NotFound';
import EventRegistrationForm from './Components/Layout/Pages/EventRegistrationForm/EventRegistrationForm1';
import PaymentStatus from './Components/Layout/Pages/EventRegistrationForm/PaymentStatus'
import StudentPaymentStatus from './Components/Layout/Pages/StudentPaymentStatus/StudentPaymentStatus';
import Reports from './Components/Layout/Pages/Reports/ReportListes';
import EventAdminLogin from './Components/Layout/Pages/EventAdminDashboard/EventAdminLogin';
import EventAdminOTP from './Components/Layout/Pages/EventAdminDashboard/EventAdminOTP';
import EventAdminDashboard from './Components/Layout/Pages/EventAdminDashboard/EventAdminDashboard';
import PrivacyPolicy from './Components/PrivacyPolicy';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Function to remove ALL cookies
const removeAllCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    // Set expiration to past date to delete cookie
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Also delete with domain if needed (optional)
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
  }

  // Clear any sessionStorage or localStorage if needed
  sessionStorage.clear();
  localStorage.clear();
};

// Component wrapper to access location in ProtectedRoute
const ProtectedRoute = ({ children, cookieName = 'authToken' }) => {
  const location = useLocation();
  const authToken = getCookie(cookieName);

  if (!authToken) {
    // Only remove all cookies for main app authToken check
    if (cookieName === 'authToken') {
      removeAllCookies();
      return <Navigate to="/EMS" replace />;
    }
    // For event admin, extract eventId from path and redirect to admin login
    const pathParts = location.pathname.split('/');

    // Find the index of 'event' and get the next part (eventId)
    const eventIndex = pathParts.indexOf('event');
    if (eventIndex !== -1 && eventIndex + 1 < pathParts.length) {
      const eventId = pathParts[eventIndex + 1];
      return <Navigate to={`/register/${eventId}/admin`} replace />;
    }

    // Fallback if we can't extract eventId
    return <Navigate to="/EMS" replace />;
  }

  return children;
};

// Public route wrapper - redirects to dashboard if already logged in
const PublicRoute = ({ children, checkFor = 'authToken' }) => {
  const authToken = getCookie(checkFor);

  if (authToken) {
    if (checkFor === 'a_t') {
      // If event admin is already logged in, redirect to event admin dashboard
      // Extract eventId from current URL if available
      const pathParts = window.location.pathname.split('/');
      const eventIdIndex = pathParts.findIndex(part => part === 'event') + 1;
      const eventId = pathParts[eventIdIndex];

      if (eventId) {
        return <Navigate to={`/admin/event/${eventId}/dashboard`} replace />;
      } else {
        // Try to get eventId from the login page URL
        const currentPath = window.location.pathname;
        const eventIdFromUrl = currentPath.split('/')[2]; // /register/:eventId/admin
        if (eventIdFromUrl && eventIdFromUrl !== 'admin') {
          return <Navigate to={`/admin/event/${eventIdFromUrl}/dashboard`} replace />;
        }
      }
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};


const ProtectedLayoutRoute = ({ children, allowedRoles }) => {
  const [userProfile, setUserProfile] = useState(null);

  const decryptValue = (encryptedValue) => {
    if (!encryptedValue) return null;
    try {
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'your-secret-key';
      const bytes = CryptoJS.AES.decrypt(encryptedValue, encryptionKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error("Decryption error:", error);
      return null;
    }
  };

  useEffect(() => {
    // Get user data from cookies on mount
    const userData = {
      role_name: decryptValue(Cookies.get('from')), // This should be "Administrator"
      user_name: decryptValue(Cookies.get('u_nm')), // User's name
      user_id: decryptValue(Cookies.get('u_id')), // User ID
      inst_id: decryptValue(Cookies.get('i_id')), // Institution ID
      inst_name: decryptValue(Cookies.get('i_nm')), // Institution name
      role_id: decryptValue(Cookies.get('r_id')), // Role ID
      isFirstLogin: decryptValue(Cookies.get('f_log')) === 'true' // First login status
    };
    setUserProfile(userData);
  }, []);

  // Role-based access check
  const checkRoleAccess = () => {
    if (!allowedRoles || !userProfile) return true;

    const userRole = userProfile.role_name;
    if (!userRole) return false;

    const normalizedRole = userRole.toUpperCase();
    return allowedRoles.includes(normalizedRole);
  };

  // If role-based access is specified and user doesn't have access, redirect to 404
  if (allowedRoles && !checkRoleAccess()) {
    return <Navigate to="/404" replace />;
  }

  return (
    <ProtectedRoute cookieName="authToken">
      <Layout userProfile={userProfile}>
        {children}
      </Layout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <Router basename="/EMS">
      <Routes>
        {/* Public Pages */}
        <Route path="/" element={
          <PublicRoute checkFor="authToken">
            <Login />
          </PublicRoute>
        } />
        <Route path="/ForgetPassword" element={
          <PublicRoute checkFor="authToken">
            <ForgetPassword />
          </PublicRoute>
        } />
        <Route path="/OtpVerify" element={
          <PublicRoute checkFor="authToken">
            <OtpVerify />
          </PublicRoute>
        } />
        <Route path="/ResetPassword" element={
          <PublicRoute checkFor="authToken">
            <ResetPassword />
          </PublicRoute>
        } />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route path="/404" element={<NotFound />} />

        <Route path="/register/:eventId" element={
          <EventRegistrationForm />
        } />

        {/* Event Admin Routes - Public */}
        <Route path="/register/:eventId/admin" element={
          <PublicRoute checkFor="a_t">
            <EventAdminLogin />
          </PublicRoute>
        } />

        <Route path="/admin/event/:eventId/otp" element={
          <PublicRoute checkFor="a_t">
            <EventAdminOTP />
          </PublicRoute>
        } />

        {/* Event Admin Dashboard - Protected with a_t cookie */}
        <Route path="/admin/event/:eventId/dashboard" element={
          <ProtectedRoute cookieName="a_t">
            <EventAdminDashboard />
          </ProtectedRoute>
        } />

        <Route path="/payment-status" element={
          <PaymentStatus />
        } />
        <Route path="/StudentPaymentStatus" element={
          <StudentPaymentStatus />
        } />

        {/* Main App Routes - Protected with authToken cookie, Layout, and Role-Based Access */}

        {/* Dashboard - Accessible by ADMINISTRATOR, ACCOUNTANT, COORDINATOR */}
        <Route path="/dashboard" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"]}>
            <Dashboard />
          </ProtectedLayoutRoute>
        } />

        {/* Events - Only ADMINISTRATOR and COORDINATOR */}
        <Route path="/events" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"]}>
            <Events />
          </ProtectedLayoutRoute>
        } />

        {/* Payment Overview - Only ADMINISTRATOR and ACCOUNTANT */}
        <Route path="/payment" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"]}>
            <PaymentOverView />
          </ProtectedLayoutRoute>
        } />

        {/* Attribute Master - Only ADMINISTRATOR */}
        <Route path="/attributeMaster" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR"]}>
            <AttributeMaster />
          </ProtectedLayoutRoute>
        } />

        {/* Coordinator Details - Only ADMINISTRATOR */}
        <Route path="/coordinatorDetails" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR"]}>
            <CoordinatorDetails />
          </ProtectedLayoutRoute>
        } />

        {/* Reports - Only ADMINISTRATOR and ACCOUNTANT */}
        <Route path="/Reports" element={
          <ProtectedLayoutRoute allowedRoles={["ADMINISTRATOR", "ACCOUNTANT", "TRUSTEE"]}>
            <Reports />
          </ProtectedLayoutRoute>
        } />

        {/* Catch all other routes - Redirect to 404 */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Router>
  );
}

export default App;